import { CalendarEventType } from '@weenat/client/dist/core/calendars'
import { defaultTimezone } from '@weenat/client/dist/core/history'
import { WeenatColor } from '@weenat/theme'
import Text from 'app/src/kit/Text'
import noop from 'lodash-es/noop'
import moment from 'moment-timezone'
import { styled } from 'styled-components'
import CalendarEvent from './CalendarEvent'

const MIN_HEIGHT = 42

interface CalendarDayContainerProps {
  $isDisabled: boolean
  $isFirstOfSelection?: boolean
  $isLastOfSelection?: boolean
  $isSelected?: boolean
  $variant?: 'compact' | 'full'
  $height: number
  $isFromAnotherMonth?: boolean
  $isToday?: boolean
}

const CalendarDayContainer = styled(Flex)<CalendarDayContainerProps>`
  flex-direction: column;
  display: flex;

  justify-content: ${(p) => (p.$variant === 'full' ? 'flex-start' : 'center')};
  align-items: 'flex-start';
  padding: 4px 8px;
  height: ${(props) => `${props.$height}px`};
  border-radius: ${({ $isLastOfSelection, $isFirstOfSelection, $variant, $isToday, $isSelected }) =>
    `${$isFirstOfSelection || $variant === 'full' || ($isToday && !$isSelected) ? '4px' : '0px'} ${
      $isLastOfSelection || $variant === 'full' || ($isToday && !$isSelected) ? '4px' : '0px'
    } ${$isLastOfSelection || $variant === 'full' || ($isToday && !$isSelected) ? '4px' : '0px'} ${
      $isFirstOfSelection || $variant === 'full' || ($isToday && !$isSelected) ? '4px' : '0px'
    }`};
  background-color: ${(props) =>
    props.$isSelected
      ? props.theme.colors.primary[200]
      : props.$isDisabled
        ? props.theme.colors.grayscale[200]
        : props.$variant === 'full'
          ? props.theme.colors.grayscale[100]
          : props.$isToday
            ? '#EDECFE'
            : 'transparent'};

  &:hover {
    border-radius: 4px;
    background-color: ${(props) =>
      props.$isSelected
        ? props.theme.colors.primary[300]
        : props.$isDisabled
          ? props.theme.colors.grayscale[300]
          : props.$variant === 'full'
            ? props.theme.colors.grayscale[50]
            : props.$isToday
              ? '#EDECFE'
              : 'transparent'};

    outline: 1px solid
      ${(props) =>
        props.$variant === 'compact'
          ? props.theme.colors.primary[500]
          : props.theme.colors.grayscale[300]};
    outline-offset: -1px;
  }

  cursor: ${(props) =>
    props.$isDisabled ? 'not-allowed' : props.$variant === 'compact' ? 'pointer' : 'cell'};
`

interface CalendarDayProps {
  day: Date
  events?: CalendarEventType[]
  onPress: () => void
  onEventPress?: (e: CalendarEventType) => void
  timezone: string
  /** Whether or not the user can interact with the day */
  isDisabled: boolean
  /** Whether or not the day is the first day of the selected period */
  isFirstOfSelection?: boolean
  /** Whether or not the day is the last day of the selected period */
  isLastOfSelection?: boolean
  /** Whether or not the day part of the selected period */
  isSelected?: boolean
  /** How should the day be displayed */
  variant?: 'compact' | 'full'
  /** Height of the component */
  height: number
  /** Whether or not the date is part of the currently displayed month */
  isFromAnotherMonth?: boolean
}

const CalendarDay: FC<CalendarDayProps> = ({
  day,
  events = [],
  isDisabled = false,
  isFirstOfSelection,
  isFromAnotherMonth,
  isLastOfSelection,
  isSelected = false,
  onEventPress = noop,
  onPress,
  timezone = defaultTimezone,
  variant = 'compact',
  height = MIN_HEIGHT
}) => {
  const today = moment().tz(timezone)
  const isToday = moment.tz(day, timezone).isSame(today, 'date')

  const dayNumber = day.getDate().toString()
  const dayNumberColor: WeenatColor =
    isToday && !isSelected
      ? 'data.protected'
      : isDisabled
        ? 'grayscale.100'
        : isFromAnotherMonth
          ? 'grayscale.300'
          : 'grayscale.black'

  const isCompactVariant = variant === 'compact'
  return (
    <Box
      onClick={
        isDisabled
          ? undefined
          : (e) => {
              onPress()
              e.stopPropagation()
            }
      }
    >
      <CalendarDayContainer
        $isDisabled={isDisabled}
        $isSelected={isSelected}
        $isFirstOfSelection={isFirstOfSelection}
        $isLastOfSelection={isLastOfSelection}
        $isFromAnotherMonth={isFromAnotherMonth}
        $variant={variant}
        $m={isCompactVariant ? 0 : 'xs'}
        $height={height}
        $isToday={isToday}
      >
        {isCompactVariant ? (
          <Text $fontWeight='bold' $textAlign='center' $color={dayNumberColor}>
            {dayNumber}
          </Text>
        ) : (
          <Flex $width='100%' $height='100%' $flexDirection='column'>
            <Box $alignSelf='end'>
              <Text $fontWeight='bold' $textAlign='right' $color={dayNumberColor}>
                {dayNumber}
              </Text>
            </Box>
            {events.map((event) => {
              return <CalendarEvent event={event} onPress={onEventPress} key={event.id} />
            })}
          </Flex>
        )}
      </CalendarDayContainer>
    </Box>
  )
}

export default CalendarDay
